import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { plural } from "@lingui/core/macro";
import { HelpCircle, CheckCircle2, Clock, AlertCircle, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCookingSessionCapacity } from "@/hooks/cooking-sessions/queries";
import { HowCalculatedSheet } from "./HowCalculatedSheet";
import type { CookingSessionSource } from "@/types/cooking-session";

interface WorkingAlertsProps {
  locationId?: number;
  source?: CookingSessionSource;
}

interface AlertRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  primaryAction?: { label: string; onClick: () => void; variant?: "default" | "destructive" };
  secondaryAction?: { label: string; onClick: () => void };
}

function AlertRow({ icon, title, description, primaryAction, secondaryAction }: AlertRowProps) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-(--color-border) bg-(--color-surface-content) px-4 py-3">
      <div className="shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-(--color-text-primary)">{title}</p>
        <p className="text-xs text-(--color-text-primary)/50 mt-0.5">{description}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {secondaryAction && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs"
            onClick={secondaryAction.onClick}
          >
            {secondaryAction.label}
          </Button>
        )}
        {primaryAction && (
          <Button
            variant={primaryAction.variant === "destructive" ? "destructive" : "default"}
            size="sm"
            className="h-8 px-3 text-xs"
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </Button>
        )}
      </div>
    </div>
  );
}

export function WorkingAlerts({ locationId, source }: WorkingAlertsProps) {
  const { t } = useLingui();
  const { data } = useCookingSessionCapacity({ locationId, source });
  const [sheetOpen, setSheetOpen] = useState(false);

  const status = data?.status ?? 'on_track';
  const bufferMinutes = data?.bufferMinutes ?? 0;
  const pausedCount = data?.pausedCount ?? 0;
  const lateCount = data?.lateCount ?? 0;

  const statusRow = {
    on_track: {
      icon: (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-(--color-success-bg) border border-(--color-success-text)/25">
          <CheckCircle2 className="h-4 w-4 text-(--color-success-text)" />
        </div>
      ),
      title: t`On track`,
      description: t`You have ~${bufferMinutes} minutes of buffer at the current efficiency.`,
    },
    at_risk: {
      icon: (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-(--color-warning-bg) border border-(--color-warning-text)/25">
          <AlertCircle className="h-4 w-4 text-(--color-warning-text)" />
        </div>
      ),
      title: t`At risk`,
      description: t`Buffer is low — only ~${bufferMinutes} minutes remain at the current efficiency.`,
    },
    behind: {
      icon: (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-(--color-danger-bg) border border-(--color-danger-text)/25">
          <AlertCircle className="h-4 w-4 text-(--color-danger-text)" />
        </div>
      ),
      title: t`Behind`,
      description: t`You are ~${Math.abs(bufferMinutes)} minutes behind at the current efficiency.`,
    },
    // Not a risk, an absence (T-1104). Capacity is chefs times the window, so
    // with nobody on the line there is no buffer to be low.
    no_chefs: {
      icon: (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-(--color-surface-muted) border border-(--color-border)">
          <UserX className="h-4 w-4 text-(--color-text-secondary)" />
        </div>
      ),
      title: t`No chefs assigned`,
      description: t`Nobody is on the line, so there is no capacity to measure the queue against.`,
    },
  } as const;

  const { icon: statusIcon, title: statusTitle, description: statusDescription } = statusRow[status];

  return (
    <>
    <div className="flex-1 min-w-0 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-(--color-text-primary)">{t`Alerts`}</h2>
          <p className="text-sm text-(--color-text-primary)/50 mt-0.5">
            {t`Actionable risks completing the prep list on time.`}
          </p>
        </div>
        <button
          type="button"
          className="flex items-center gap-1.5 text-xs text-(--color-text-primary)/40 hover:text-(--color-text-primary) transition-colors"
          onClick={() => setSheetOpen(true)}
        >
          <HelpCircle className="h-4 w-4" />
          {t`How it's calculated`}
        </button>
      </div>

      {/* Alert rows */}
      <div className="flex flex-col gap-2">
        {/* Status row — reflects data.status */}
        <AlertRow
          icon={statusIcon}
          title={statusTitle}
          description={statusDescription}
          secondaryAction={{ label: t`Details`, onClick: () => {} }}
        />

        {/* Paused items */}
        {pausedCount > 0 && (
          <AlertRow
            icon={
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-(--color-warning-bg) border border-(--color-warning-text)/25">
                <Clock className="h-4 w-4 text-(--color-warning-text)" />
              </div>
            }
            title={plural(pausedCount, { one: `# item paused`, other: `# items paused` })}
            description={t`Paused items slow throughput & increase risk.`}
            secondaryAction={{ label: t`Details`, onClick: () => {} }}
            primaryAction={{ label: t`Resolve`, onClick: () => {} }}
          />
        )}

        {/* Late items */}
        {lateCount > 0 && (
          <AlertRow
            icon={
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-(--color-danger-bg) border border-(--color-danger-text)/25">
                <AlertCircle className="h-4 w-4 text-(--color-danger-text)" />
              </div>
            }
            title={plural(lateCount, { one: `# item already late`, other: `# items already late` })}
            description={t`Target time exceeded. Shift lower-priority items.`}
            secondaryAction={{ label: t`Details`, onClick: () => {} }}
            primaryAction={{ label: t`De-scope`, onClick: () => {}, variant: "destructive" }}
          />
        )}
      </div>
    </div>
    <HowCalculatedSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  );
}
